"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { type UserContext, shouldDisplayRole } from "@/lib/permissions";
import { getHomeSnapshot, type AdmissionsCounts } from "@/lib/dashboard/api";

export type MemberSection =
    | "home"
    | "profile"
    | "groups"
    | "gigs"
    | "work"
    | "notices"
    | "recognition"
    | "directory"
    | "policies"
    | "admissions-committee"
    | "admissions-board"
    | "org-groups"
    | "org-volunteers";

const NAV_ITEMS: { id: MemberSection; label: string; icon: string; description: string }[] = [
    { id: "home",    label: "Home",         icon: "home",       description: "What needs you"           },
    { id: "groups",  label: "My Groups",    icon: "group_work", description: "Groups & console"          },
    { id: "gigs",    label: "My Gigs",      icon: "handyman",   description: "Short-term volunteer tasks" },
    { id: "work",    label: "Activities",   icon: "bolt",       description: "Tasks & events"             },
    // No "Policies" entry: the policy surfaces are pulled back from volunteers
    // for now — staff keep the Policy Console in the staff portal.
    // `?section=policies` still resolves, so links already sent out keep working.
    // No "Notice Board" entry: the bell in the top bar is the one "what's new"
    // metaphor, and a left-nav twin of it meant two surfaces to keep in step.
    // `?section=notices` still resolves — the bell's "View all notices" and the
    // Home strip both open it.
    { id: "directory",label: "Volunteer Directory",    icon: "analytics",  description: "Member search & analytics"  },
    // No "Admissions Review" entry: the committee's queue lives on the Admissions
    // Committee's own group dashboard (`?tab=admissions`), reached from My Groups.
    // Two doors to one queue meant two places to keep in step; the badge on
    // "My Groups" above carries the count instead. `?section=admissions-committee`
    // still resolves — dashboard/page.tsx redirects it — so links already sent
    // out by email keep working.
    // No "Board Ratification" entry either, for the same reason and by the same
    // rule: the Board's queue is a tab on the Board of Directors' own group
    // dashboard (`?tab=board`). `?section=admissions-board` still resolves —
    // dashboard/page.tsx redirects it — so ratification emails already sent out
    // keep working.
];

const LINK_ITEMS: { id: MemberSection; label: string; icon: string; description: string }[] = [
    { id: "org-groups",     label: "Groups",     icon: "diversity_3",    description: "Browse all groups"    },
    // "Volunteer Snapshot", not "Volunteers": this is the public org chart of
    // who holds which seat, not the gated member directory two entries above.
    { id: "org-volunteers", label: "Volunteer Snapshot", icon: "people",  description: "Who holds which role" },
];

const SECTION_TITLES: Record<MemberSection, string> = {
    home:        "Home",
    profile:     "My Profile",
    groups:      "My Groups",
    gigs:        "My Gigs",
    work:        "Activities",
    notices:     "Notice Board",
    recognition: "Recognition",
    directory:   "Volunteer Directory",
    policies:    "Policies",
    "admissions-committee": "Admissions Review",
    "admissions-board":     "Board Decisions",
    "org-groups":     "Groups",
    "org-volunteers": "Volunteer Snapshot",
};

export { SECTION_TITLES };

interface Props {
    active: MemberSection;
    onSelect: (s: MemberSection) => void;
    user: UserContext;
    isLeader: boolean;
    onLogout: () => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

export function MemberSidebar({ active, onSelect, user, onLogout, mobileOpen, onMobileClose }: Props) {
    const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
    const [isCollapsedRaw, setIsCollapsedRaw] = useState(false);
    const [isDesktop, setIsDesktop] = useState(true);
    // Collapse (icon-only mode) only applies on desktop. On mobile the drawer is
    // always the full 260px panel with labels, regardless of the persisted setting.
    const isCollapsed = isCollapsedRaw && isDesktop;

    // Track desktop breakpoint (matches Tailwind `lg`)
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    // Load collapsed state from localStorage on mount
    useEffect(() => {
        const val = localStorage.getItem("cmta_member_sidebar_collapsed");
        if (val === "true") {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setIsCollapsedRaw(true);
        }
    }, []);

    // Both admissions queues live on their own group dashboards now, so this is
    // read for the counts rather than for visibility. Failure is treated as
    // "not a member", which shows no badge rather than a wrong one.
    //
    // The counts now ride the Home snapshot rather than a second
    // `GET /admissions-committee/access` of their own: this and HomePanel were
    // each firing that call on every render, and the shared 30s cache in
    // `getHomeSnapshot` means the sidebar costs nothing on top of Home.
    const [admissionsAccess, setAdmissionsAccess] = useState<AdmissionsCounts | null>(null);
    useEffect(() => {
        let cancelled = false;
        getHomeSnapshot()
            .then((s) => { if (!cancelled) setAdmissionsAccess(s.admissions); })
            .catch(() => { if (!cancelled) setAdmissionsAccess(null); });
        return () => { cancelled = true; };
    }, []);

    // Both counts ride the same badge: they point at two different group
    // dashboards, but "My Groups" is the one trailhead to either, so the number
    // there has to be everything waiting behind it.
    //
    // Staff may read the committee's queue but never vote in it, so
    // `awaiting_count` is already 0 for them server-side, and
    // `board_awaiting_count` is only populated for an actual board member —
    // this stays truthful without a second check either side.
    const admissionsAwaiting =
        (admissionsAccess?.awaiting_count ?? 0) + (admissionsAccess?.board_awaiting_count ?? 0);

    const toggleCollapse = () => {
        const newVal = !isCollapsedRaw;
        setIsCollapsedRaw(newVal);
        localStorage.setItem("cmta_member_sidebar_collapsed", String(newVal));
    };

    // Every remaining entry is unconditional: the two admissions surfaces were
    // the only capability-gated rows, and both are group tabs now.
    const visibleItems = NAV_ITEMS;

    const handleSelect = (s: MemberSection) => {
        onSelect(s);
        onMobileClose?.();
    };

    return (
        <>
            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-[55] lg:hidden"
                    onClick={onMobileClose}
                />
            )}

            <aside
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                className={`
                    ${isCollapsed ? "lg:w-[68px]" : "lg:w-[260px]"} w-[260px] flex-shrink-0 h-screen bg-[#003d45] text-white flex flex-col
                    fixed lg:sticky top-0 left-0 z-[60] lg:translate-x-0 transition-transform duration-300 ease-in-out overflow-visible
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                `}
            >
                {/* Brand Header with Integrated Toggle */}
                <div className={`py-5 border-b border-white/10 flex flex-col items-center justify-center relative ${isCollapsed ? "px-0" : "px-6"}`}>
                    {!isCollapsed ? (
                        <div className="w-full flex items-center justify-between gap-3">
                            <Link href="/dashboard" className="flex items-center gap-3 no-underline group min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                                    <span className="material-symbols-outlined text-white text-xl">volunteer_activism</span>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-black uppercase tracking-[0.18em] text-white/90 leading-tight whitespace-nowrap">Member Portal</p>
                                    <p className="text-[10px] text-white/40 font-medium mt-0.5 whitespace-nowrap">CMT Association</p>
                                </div>
                            </Link>
                            <button
                                onClick={toggleCollapse}
                                className="hidden lg:flex w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 text-white/70 hover:text-white items-center justify-center transition-colors flex-shrink-0 group relative"
                                title="Collapse sidebar"
                            >
                                <span className="material-symbols-outlined text-base">menu_open</span>
                                <span className="absolute left-[32px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                                    Collapse Sidebar
                                </span>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={toggleCollapse}
                            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/15 text-white flex items-center justify-center transition-colors group relative"
                            title="Expand sidebar"
                        >
                            <span className="material-symbols-outlined text-xl">menu</span>
                            <span className="absolute left-[58px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                                Expand Sidebar
                            </span>
                        </button>
                    )}
                </div>

                {/* Nav */}
                <nav id="member-sidebar-nav" className={`flex-1 py-4 space-y-1.5 overflow-y-auto [&::-webkit-scrollbar]:hidden ${isCollapsed ? "px-2" : "px-3"}`}>
                    <p className={`text-[9px] font-black uppercase tracking-[0.22em] text-white/30 pt-2 pb-3 transition-all duration-300 ${isCollapsed ? "text-center px-0 overflow-hidden text-[0px] h-0 opacity-0" : "px-3"}`}>
                        Navigation
                    </p>
                    {visibleItems.map((item) => {
                        const isActive = active === item.id;
                        // "My Groups" carries the admissions and board counts because
                        // both queues now live inside a group, so that row is the
                        // trailhead to either.
                        const badgeCount = item.id === "groups" ? admissionsAwaiting : 0;
                        return (
                            <Link
                                key={item.id}
                                href={`/dashboard?section=${item.id}`}
                                onClick={(e) => {
                                    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSelect(item.id);
                                    }
                                }}
                                className={`flex items-center transition-all group relative no-underline ${
                                    isCollapsed 
                                        ? "w-11 h-11 mx-auto rounded-xl justify-center" 
                                        : "w-full flex-row gap-3 px-3 py-3 rounded-xl"
                                } ${
                                    isActive
                                        ? "bg-white/15 border border-white/20 shadow-inner"
                                        : "hover:bg-white/8 border border-transparent"
                                }`}
                            >
                                <div className={`flex items-center justify-center flex-shrink-0 transition-colors ${
                                    isCollapsed ? "w-8 h-8 rounded-lg" : "w-8 h-8 rounded-lg"
                                } ${
                                    !isCollapsed && isActive ? "bg-white/20" : !isCollapsed ? "bg-white/5 group-hover:bg-white/10" : ""
                                } relative`}>
                                    <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
                                    {isCollapsed && badgeCount > 0 && !isActive && (
                                        <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-[#ae232d] text-white text-[8px] font-black flex items-center justify-center px-0.5">
                                            {badgeCount > 9 ? "9+" : badgeCount}
                                        </span>
                                    )}
                                </div>
                                <div className={`min-w-0 transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden pointer-events-none flex-shrink-0" : "flex-1 w-auto opacity-100 ml-3 text-left"}`}>
                                    <p className={`text-sm font-bold leading-tight whitespace-nowrap ${isActive ? "text-white" : "text-white/70 group-hover:text-white"} transition-colors`}>
                                        {item.label}
                                    </p>
                                    <p className={`text-[10px] leading-tight mt-0.5 whitespace-nowrap ${isActive ? "text-white/60" : "text-white/35 group-hover:text-white/50"} transition-colors`}>
                                        {item.description}
                                    </p>
                                </div>
                                {isActive && !isCollapsed && (
                                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#7ad4e5] flex-shrink-0" />
                                )}
                                {badgeCount > 0 && !isActive && !isCollapsed && (
                                    <span className="ml-auto min-w-[18px] h-[18px] rounded-full bg-[#ae232d] text-white text-[9px] font-black flex items-center justify-center px-1 flex-shrink-0">
                                        {badgeCount > 9 ? "9+" : badgeCount}
                                    </span>
                                )}

                                {/* Premium custom tooltip for collapsed view (flowing beautifully onto content) */}
                                {isCollapsed && (
                                    <span className="absolute left-[58px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                                        {item.label}
                                    </span>
                                )}
                            </Link>
                        );
                    })}

                    {/* Divider */}
                    <div className={`my-2 border-t border-white/10 ${isCollapsed ? "mx-1" : "mx-2"}`} />

                    {LINK_ITEMS.map((item) => {
                        const isActive = active === item.id;
                        return (
                        <Link
                            key={item.id}
                            href={`/dashboard?section=${item.id}`}
                            onClick={(e) => {
                                if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSelect(item.id);
                                }
                            }}
                            className={`flex items-center transition-all group relative no-underline border rounded-xl ${
                                isActive ? "bg-white/15 border-white/20 shadow-inner" : "hover:bg-white/8 border-transparent"
                            } ${
                                isCollapsed
                                    ? "w-11 h-11 mx-auto justify-center"
                                    : "w-full flex-row gap-3 px-3 py-3"
                            }`}
                        >
                            <div className={`flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg bg-white/5 group-hover:bg-white/10`}>
                                <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
                            </div>
                            <div className={`min-w-0 transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden pointer-events-none flex-shrink-0" : "flex-1 w-auto opacity-100 ml-3 text-left"}`}>
                                <p className="text-sm font-bold leading-tight whitespace-nowrap text-white/70 group-hover:text-white transition-colors">
                                    {item.label}
                                </p>
                                <p className="text-[10px] leading-tight mt-0.5 whitespace-nowrap text-white/35 group-hover:text-white/50 transition-colors">
                                    {item.description}
                                </p>
                            </div>
                            {isCollapsed && (
                                <span className="absolute left-[58px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                                    {item.label}
                                </span>
                            )}
                        </Link>
                        );
                    })}
                </nav>

                {/* Footer */}
                <div className={`py-4 border-t border-white/10 mt-auto ${isCollapsed ? "px-2 text-center space-y-4" : "px-4 space-y-3"}`}>
                    <div className={`flex items-center ${isCollapsed ? "flex-col gap-3 justify-center" : "gap-3"}`}>
                        {user.profile_photo_url ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={user.profile_photo_url} alt="avatar" className="w-9 h-9 rounded-full object-cover border border-white/20 flex-shrink-0" />
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-xs font-black flex-shrink-0">
                                {initials}
                            </div>
                        )}
                        <div className={`min-w-0 flex-1 text-left transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden pointer-events-none hidden" : "w-auto opacity-100 block"}`}>
                            <p className="text-sm font-bold text-white truncate">{user.first_name} {user.last_name}</p>
                            {shouldDisplayRole(user.role) && (
                                <p className="text-[10px] text-white/40 uppercase tracking-widest font-black">{user.role}</p>
                            )}
                        </div>
                        <button
                            onClick={onLogout}
                            title="Sign out"
                            className={`text-white/30 hover:text-white transition-colors flex-shrink-0 ${isCollapsed ? "p-2 hover:bg-white/10 rounded-lg" : ""}`}
                        >
                            <span className="material-symbols-outlined text-base">logout</span>
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
}
